<script setup lang='ts'>
import { Head, Link, router } from '@inertiajs/vue3'
import Button from '@/Components/Utils/Button.vue'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import { useFormatTime } from '@/Composables/useFormatTime'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { inject, ref, watch, computed } from 'vue'
import axios from 'axios'
import { notify } from '@kyvg/vue3-notification'
import { useTruncate } from '@/Composables/useTruncate'
import LoadingIcon from '@/Components/Utils/LoadingIcon.vue'
import Tag from '@/Components/Utils/Tag.vue'
import { routeType } from '@/types/route'
import { aikuLocaleStructure } from '@/Composables/useLocaleStructure'
import Icon from '@/Components/Utils/Icon.vue'
import { DatePicker, InputNumber } from 'primevue'
import CountUp from 'vue-countup-v3'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faFilePdf, faTimes, faArrowRight, faArrowLeft, faPencil, faTrashUndo, faCalendarAlt, faDollarSign, faSearch, faCheck, faWallet } from '@fal'
import { faBadgePercent, faTag } from '@fas'
import { library } from '@fortawesome/fontawesome-svg-core'
import { InvoiceResource, OrdersResource } from '@/types/Resources'
import OrderInInvoiceMaster from '@/Components/Master/Invoices/OrderInInvoiceMaster.vue'
import Select from 'primevue/select'
library.add(faFilePdf, faTimes, faArrowRight, faArrowLeft, faPencil, faTrashUndo, faCalendarAlt, faDollarSign, faTag, faBadgePercent, faSearch, faCheck, faWallet)

const props = defineProps<{
    invoice: InvoiceResource
    updateOrderRoute: routeType
    resetOrderRoute: routeType
    paymentMethodOptions: { label: string; value: string }[]
}>()

// const locale = inject('locale', aikuLocaleStructure)
const selectedInvoice = ref<InvoiceResource | null>(null)

// Fetch Order with search
const selectedOrderId = ref<number[]>([])
const isLoadingOrder = ref(false)
const listOrder = ref<any[]>([])
const searchOrderQuery = ref('')

let searchOrderTimeout: ReturnType<typeof setTimeout> | null = null
watch(searchOrderQuery, (val) => {
    if (searchOrderTimeout) clearTimeout(searchOrderTimeout)
    searchOrderTimeout = setTimeout(() => fetchOrder(val), 350)
})

const getInvoiceCustomerId = (): number | null => {
    return props.invoice.customer?.id ?? null
}

const fetchOrder = async (q = '') => {
    isLoadingOrder.value = true
    try {
        const params: Record<string, string | number> = { q }
        const customerId = getInvoiceCustomerId()
        if (customerId) {
            params.customer_id = customerId
        }
        const { data } = await axios.get(route('json.orders.unpaid'), { params })
        listOrder.value = data.data ?? data
        return data
    } catch (error) {
        console.error('error', error)
        notify({
            title: 'Oops!',
            text: 'Gagal dalam mencari data Order',
            type: 'error',
        })
    } finally {
        isLoadingOrder.value = false
    }
}

// Section: Add order
const isModalOrderList = ref(false)
const onAddOrder = (idInvoice?: number) => {
    console.log('idinvoice', idInvoice)
    if (!idInvoice) {
        notify({
            title: 'Pilih invoice terlebih dahulu',
            text: '',
            type: 'error',
        })

        return
    }
    router.patch(
        route('casheer.invoices.add_order', [idInvoice]),
        {
            // invoice_id: props.invoice.id,
            orders: selectedOrderId.value
        },
        {
            preserveState: true,
            preserveScroll: true,
            onStart: () => isLoadingOrder.value = true,
            onFinish: () => isLoadingOrder.value = false,
            onSuccess: () => {
                isModalOrderList.value = false
            },
            onError: () => notify({
                title: 'Oops!',
                text: 'Gagal menambah order',
                type: 'error',
            }),
        }
    )
}



// Section: set to Paid
const isLoadingPaid = ref<number | null>(null)
const isPaymentModalOpen = ref(false)
const newPaymentAmount = ref(0)
const newPaymentMethod = ref('transfer')
const newPaymentNote = ref('')
const newPaymentDate = ref<Date>(new Date())

const openPaymentModal = () => {
    newPaymentAmount.value = props.invoice.amount_remaining
    newPaymentMethod.value = 'transfer'
    newPaymentNote.value = ''
    newPaymentDate.value = new Date()
    isPaymentModalOpen.value = true
}

const formatDateForApi = (date: Date): string => {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
}

const confirmAddPayment = () => {
    router.post(
        route('casheer.invoices.payments.store', [props.invoice.id]),
        {
            amount: newPaymentAmount.value,
            payment_method: newPaymentMethod.value,
            note: newPaymentNote.value || undefined,
            payment_date: formatDateForApi(newPaymentDate.value),
        },
        {
            preserveState: true,
            preserveScroll: true,
            onStart: () => isLoadingPaid.value = props.invoice.id,
            onFinish: () => { isLoadingPaid.value = null },
            onSuccess: () => {
                isPaymentModalOpen.value = false
                notify({ title: 'Success!', text: 'Pembayaran berhasil dicatat', type: 'success' })
            },
            onError: (e) => {
                const errorMsg = Object.values(e)[0] as string
                notify({ title: 'Oops!', text: errorMsg || 'Gagal mencatat pembayaran.', type: 'error' })
            },
        }
    )
}

const isLoadingDeletePayment = ref<number | null>(null)
const deletePayment = (paymentId: number) => {
    router.delete(
        route('casheer.invoices.payments.delete', [props.invoice.id, paymentId]),
        {
            preserveState: true,
            preserveScroll: true,
            onStart: () => isLoadingDeletePayment.value = paymentId,
            onFinish: () => isLoadingDeletePayment.value = null,
            onSuccess: () => notify({ title: 'Berhasil', text: 'Pembayaran dihapus', type: 'info' }),
            onError: () => notify({ title: 'Oops!', text: 'Gagal menghapus pembayaran', type: 'error' }),
        }
    )
}

const isEditPaymentModalOpen = ref(false)
const editingPaymentId = ref<number | null>(null)
const editPaymentDate = ref<Date>(new Date())
const editPaymentNote = ref('')
const isLoadingEditPayment = ref(false)

const openEditPaymentModal = (payment: InvoiceResource['payments'][number]) => {
    editingPaymentId.value = payment.id
    editPaymentDate.value = payment.payment_date ? new Date(payment.payment_date) : new Date(payment.created_at)
    editPaymentNote.value = payment.note ?? ''
    isEditPaymentModalOpen.value = true
}

const confirmEditPayment = () => {
    if (!editingPaymentId.value) return
    router.patch(
        route('casheer.invoices.payments.update', [props.invoice.id, editingPaymentId.value]),
        {
            payment_date: formatDateForApi(editPaymentDate.value),
            note: editPaymentNote.value || undefined,
        },
        {
            preserveState: true,
            preserveScroll: true,
            onStart: () => isLoadingEditPayment.value = true,
            onFinish: () => isLoadingEditPayment.value = false,
            onSuccess: () => {
                isEditPaymentModalOpen.value = false
                notify({ title: 'Berhasil', text: 'Pembayaran diperbarui', type: 'success' })
            },
            onError: (e) => {
                const errorMsg = Object.values(e)[0] as string
                notify({ title: 'Oops!', text: errorMsg || 'Gagal memperbarui pembayaran.', type: 'error' })
            },
        }
    )
}

const setToPaid = (idInvoice: number) => {
    if (!idInvoice) return
    openPaymentModal()
}

// Section: Additional cost
const isAdditionalCostModalOpen = ref(false)
const newCostAmount = ref(0)
const newCostDescription = ref('')
const newCostDate = ref<Date | null>(null)
const isLoadingAdditionalCost = ref(false)

const openAdditionalCostModal = () => {
    newCostAmount.value = 0
    newCostDescription.value = ''
    newCostDate.value = null
    isAdditionalCostModalOpen.value = true
}

const confirmAddAdditionalCost = () => {
    router.post(
        route('casheer.invoices.additional_costs.store', [props.invoice.id]),
        {
            amount: newCostAmount.value,
            description: newCostDescription.value,
            date: newCostDate.value ? formatDateForApi(newCostDate.value) : undefined,
        },
        {
            preserveState: true,
            preserveScroll: true,
            onStart: () => isLoadingAdditionalCost.value = true,
            onFinish: () => isLoadingAdditionalCost.value = false,
            onSuccess: () => {
                isAdditionalCostModalOpen.value = false
                notify({ title: 'Berhasil', text: 'Ongkos tambahan ditambahkan', type: 'success' })
            },
            onError: (e) => {
                const errorMsg = Object.values(e)[0] as string
                notify({ title: 'Oops!', text: errorMsg || 'Gagal menambah ongkos tambahan.', type: 'error' })
            },
        }
    )
}

const isLoadingDeleteAdditionalCost = ref<number | null>(null)
const deleteAdditionalCost = (costId: number) => {
    router.delete(
        route('casheer.invoices.additional_costs.delete', [props.invoice.id, costId]),
        {
            preserveState: true,
            preserveScroll: true,
            onStart: () => isLoadingDeleteAdditionalCost.value = costId,
            onFinish: () => isLoadingDeleteAdditionalCost.value = null,
            onSuccess: () => notify({ title: 'Berhasil', text: 'Ongkos tambahan dihapus', type: 'info' }),
            onError: () => notify({ title: 'Oops!', text: 'Gagal menghapus ongkos tambahan', type: 'error' }),
        }
    )
}

// Section: set to unpaid
const isLoadingUnpaid = ref<number | null>(null)
const setToUnpaid = (idInvoice: number) => {
    console.log('idinvoice', idInvoice)
    if (!idInvoice) {
        notify({
            title: 'Pilih invoice terlebih dahulu',
            text: '',
            type: 'error',
        })

        return
    }

    router.patch(
        route('casheer.invoices.mark_unpaid', [idInvoice]),
        {
            // invoice_id: props.invoice.id
        },
        {
            preserveState: true,
            preserveScroll: true,
            onStart: () => isLoadingUnpaid.value = idInvoice,
            onFinish: () => isLoadingUnpaid.value = null,
            onSuccess: () => {
                notify({
                    title: 'Success!',
                    text: 'Berhasil membatalkan pelunasan',
                    type: 'info',
                })
            },
            onError: (e) => {
                console.error('error', e)
                notify({
                    title: 'Oops!',
                    text: 'Gagal melunaskan invoice.',
                    type: 'error',
                })
            },
        }
    )
}


// Section: Export
const isLoadingExport = ref<number | null>(null);
const downloadPDF = async (idInvoice: number, fileName: string) => {
    try {
        isLoadingExport.value = idInvoice;

        // Fetch the PDF file (or trigger the server to generate it)
        const response = await axios.get(route('casheer.invoices.pdf', { id: idInvoice }), {
        responseType: 'blob', // Ensures the response is treated as a file
        });

        // Create a blob URL and trigger download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileName}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
    } catch (error) {
        console.error('Error downloading PDF:', error);
    } finally {
        isLoadingExport.value = null;
    }
}



// Section: Edit Order
const isLoadingEditOrder = ref<number | null | undefined>(null)
const isModalEditOrder = ref(false)
const selectedEditOrder = ref<OrdersResource | null>(null)
const onEditOrder = () => {
    console.log('selectedEditOrder', selectedEditOrder.value)
    if (selectedEditOrder.value) {
        router[props.updateOrderRoute?.method || 'patch'](
            route(props.updateOrderRoute.name, [props.invoice.id, selectedEditOrder.value?.id]),
            {
                product_price_in_invoice: selectedEditOrder.value?.product_price_show,
                quantity_in_invoice: selectedEditOrder.value?.quantity_show,
                discount_in_percentage: selectedEditOrder.value?.discount_in_percentage,
            },
            {
                preserveState: true,
                preserveScroll: true,
                onStart: () => isLoadingEditOrder.value = selectedEditOrder.value?.id,
                onFinish: () => isLoadingEditOrder.value = null,
                onSuccess: () => {
                    isModalEditOrder.value = false
                },
                onError: () => notify({
                    title: 'Oops!',
                    text: 'Gagal mengedit order',
                    type: 'error',
                }),
            }
        )
    } else {
        notify({
            title: 'Oops!',
            text: 'Order belum dipilih',
            type: 'error',
        })
    }
}
const onResetOrder = () => {
    if (selectedEditOrder.value) {
        // selectedEditOrder.value.quantity_show = selectedEditOrder.value.quantity
        // selectedEditOrder.value.product_price_show = selectedEditOrder.value?.product?.price

        router[props.resetOrderRoute?.method || 'patch'](
            route(props.resetOrderRoute.name, [props.invoice.id, selectedEditOrder.value?.id]),
            {
                // product_price_in_invoice: selectedEditOrder.value?.product_price_show,
                // quantity_in_invoice: selectedEditOrder.value?.quantity_show,
            },
            {
                preserveState: true,
                preserveScroll: true,
                onStart: () => isLoadingEditOrder.value = selectedEditOrder.value?.id,
                onFinish: () => isLoadingEditOrder.value = null,
                onSuccess: () => {
                    isModalEditOrder.value = false
                },
                onError: () => notify({
                    title: 'Oops!',
                    text: 'Gagal mereset diskon pada order',
                    type: 'error',
                }),
            }
        )
    } else {
        notify({
            title: 'Oops!',
            text: 'Order belum dipilih',
            type: 'error',
        })
    }
}


// Section: preview total for edit modal
const previewTotal = computed(() => {
    const order = selectedEditOrder.value
    if (!order) return null
    const qty   = Number(order.quantity_show ?? order.quantity)
    const price = Number(order.product_price_show ?? order.product_price)
    let total = qty * price
    if (order.type === 'flexo') {
        total *= (order.dimension_width ?? 1) * (order.dimension_height ?? 1)
    }
    const discount = Number(order.discount_in_percentage ?? 0)
    const afterDiscount = discount > 0 ? total * (1 - discount / 100) : total
    return { total, afterDiscount, hasDiscount: discount > 0 }
})
</script>

<template>
    <section aria-labelledby="summary-heading" class="pt-2 flex-grow max-w-4xl pb-10 px-0 text-sm tabular-nums">
        <div class="flex justify-between flex-col lg:flex-row gap-y-3 mb-3">
            <div class="self-end flex flex-wrap w-full justify-end gap-x-2 gap-y-1">
                <Button
                    v-if="invoice.billing_status !== 'paid'"
                    @click="openPaymentModal"
                    label="Tambah Pembayaran"
                    icon="fal fa-dollar-sign"
                    :loading="isLoadingPaid === invoice.id"
                    size="l"
                    type="positive"
                    class="flex-grow lg:flex-grow-0 !bg-green-50 hover:!bg-green-200"
                />
                <Button
                    v-if="invoice.billing_status !== 'unpaid'"
                    @click="() => setToUnpaid(invoice.id)"
                    label="Batalkan Semua Pembayaran"
                    type="negative"
                    size="l"
                    :loading="isLoadingUnpaid === invoice.id"
                />
                <!-- <Button
                    @click="() => downloadPDF(invoice.id, invoice.reference)"
                    label="Export to PDF"
                    :style="'tertiary'"
                    icon="fal fa-file-pdf"
                    size="l"
                    :loading="isLoadingExport == invoice.id"
                /> -->
            </div>
        </div>

        <div class="mt-3">
            <TransitionGroup tag="ul" name="list"  class="border border-gray-200 border-l-0 font-medium">
                <template 
                    v-for="order, idxOrder in invoice.orders"
                    :key="order.id"
                >
                    <OrderInInvoiceMaster
                        :index="idxOrder"
                        :order
                        :isLoadingEditOrder
                        :invoice
                        :selectedEditOrder="selectedEditOrder"
                        :isModalEditOrder="isModalEditOrder"
                        @selectedEditOrder="(e: OrdersResource) => (selectedEditOrder = e)"
                        @isModalEditOrder="(e) => (isModalEditOrder = e)"
                    />
                </template>
            </TransitionGroup>

            <!-- Button: tambah order -->
            <div class="my-4">
                <Button v-if="invoice.billing_status === 'unpaid'" @click="() => (selectedOrderId = [], searchOrderQuery = '', isModalOrderList = true, selectedInvoice = invoice, fetchOrder())" full :style="'dashed'" label="Tambah order" icon="fal fa-plus" />
            </div>

            <div class="my-4 border-t border-gray-500"></div>

            <!-- Additional costs -->
            <div vxif="invoice.additional_costs?.length" class="my-2 border border-gray-200 rounded-lg overflow-hidden">
                <div class="bg-gray-50 px-3 py-2 flex justify-between items-center">
                    <div class="text-xs font-semibold xtext-gray-500 uppercase tracking-wide">Ongkos Tambahan</div>
                    
                    <div>
                        <Button
                            @click="openAdditionalCostModal"
                            label="Tambah Ongkos"
                            icon="fal fa-plus"
                            size="xs"
                            key=2
                            :style="'tertiary'"
                            class="flex-grow lg:flex-grow-0"
                        />
                    </div>
                </div>
                <div v-if="invoice.additional_costs?.length" class="divide-y divide-gray-100">
                    <div v-for="cost in invoice.additional_costs" :key="cost.id" class="flex items-center justify-between px-3 py-2 text-sm">
                        <div class="flex items-center gap-3 min-w-0">
                            <span class="font-medium text-gray-700 truncate">{{ cost.description }}</span>
                            <span v-if="cost.date" class="text-gray-400 shrink-0">{{ useFormatTime(cost.date) }}</span>
                        </div>
                        <div class="flex items-center gap-3 shrink-0">
                            <span class="font-semibold text-gray-700">+{{ useFormatCurrency(cost.amount) }}</span>
                            <button
                                v-tooltip="'Hapus ongkos ini'"
                                @click="deleteAdditionalCost(cost.id)"
                                :disabled="isLoadingDeleteAdditionalCost === cost.id"
                                class="text-red-400 hover:text-red-600 disabled:opacity-50 transition"
                            >
                                <FontAwesomeIcon icon="fal fa-times" fixed-width aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                </div>
                <div v-else class="py-2 text-center text-gray-400">
                    Tidak ada ongkos tambahan
                </div>
            </div>

            <!-- Section: Total & Payments -->
            <dl v-if="invoice.additional_costs?.length" class="flex items-center justify-between pt-3 px-3 text-sm text-gray-500">
                <dt>Subtotal Order</dt>
                <dd class="tabular-nums">{{ useFormatCurrency(invoice.final_total - invoice.additional_costs_total) }}</dd>
            </dl>
            <dl v-if="invoice.additional_costs?.length" class="flex items-center justify-between pt-1 px-3 text-sm text-gray-500">
                <dt>Total Ongkos Tambahan</dt>
                <dd class="tabular-nums">{{ useFormatCurrency(invoice.additional_costs_total) }}</dd>
            </dl>
            <dl class="flex items-center justify-between py-3 text-lg font-medium px-3 border-t border-dashed border-gray-300"
                :class="invoice.additional_costs?.length ? 'mt-1' : ''"
            >
                <dt class="">Total</dt>
                <dd class="font-bold tabular-nums">
                    <CountUp :endVal="invoice.final_total || 0" :duration="1.2" :scrollSpyOnce="true"
                        :options="{
                        formattingFn: (number: number) => useFormatCurrency(number)
                    }" />
                </dd>
            </dl>

            <!-- Payment history -->
            <div v-if="invoice.payments?.length" class="mt-2 border border-gray-200 rounded-lg overflow-hidden">
                <div class="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Riwayat Pembayaran</div>
                <div class="divide-y divide-gray-100">
                    <div v-for="payment in invoice.payments" :key="payment.id" class="flex items-center justify-between px-3 py-2 text-sm">
                        <div class="flex items-center gap-3">
                            <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-sky-100 text-sky-700">{{ payment.payment_method_label }}</span>
                            <span class="text-gray-500">{{ useFormatTime(payment.payment_date ?? payment.created_at) }}</span>
                            <span v-if="payment.note" class="text-gray-400 italic">{{ payment.note }}</span>
                        </div>
                        <div class="flex items-center gap-3">
                            <span class="font-semibold text-green-700">+{{ useFormatCurrency(payment.amount) }}</span>
                            <button
                                v-tooltip="'Edit pembayaran'"
                                @click="openEditPaymentModal(payment)"
                                class="text-sky-400 hover:text-sky-600 transition"
                            >
                                <FontAwesomeIcon icon="fal fa-pencil" fixed-width aria-hidden="true" />
                            </button>
                            <button
                                v-tooltip="'Hapus pembayaran ini'"
                                @click="deletePayment(payment.id)"
                                :disabled="isLoadingDeletePayment === payment.id"
                                class="text-red-400 hover:text-red-600 disabled:opacity-50 transition"
                            >
                                <FontAwesomeIcon icon="fal fa-times" fixed-width aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                </div>
                <div class="flex items-center justify-between px-3 py-2 bg-gray-50 border-t border-gray-200 text-sm font-semibold">
                    <span>Sudah Dibayar</span>
                    <span class="text-green-700">{{ useFormatCurrency(invoice.amount_paid) }}</span>
                </div>
                <div v-if="invoice.amount_remaining > 0" class="flex items-center justify-between px-3 py-2 bg-amber-50 text-sm font-semibold text-amber-800">
                    <span>Sisa Tagihan</span>
                    <span>{{ useFormatCurrency(invoice.amount_remaining) }}</span>
                </div>
            </div>
        </div>

        <!-- Modal: add Order -->
        <Dialog
            v-model:visible="isModalOrderList"
            modal
            header="Tambah Order ke Invoice"
            :style="{ width: '32rem' }"
            dismissableMask
        >
            <!-- Search input -->
            <div class="mb-3">
                <label class="block text-sm font-medium mb-1">Cari Order</label>
                <div class="relative">
                    <FontAwesomeIcon icon="fal fa-search" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
                    <InputText
                        v-model="searchOrderQuery"
                        placeholder="No. order, nama pelanggan, hari, bulan, tahun…"
                        class="w-full !pl-9"
                    />
                </div>
            </div>

            <!-- Order list -->
            <div class="border border-gray-200 rounded-md overflow-y-auto max-h-72 min-h-20">
                <div v-if="isLoadingOrder" class="flex items-center justify-center h-20 text-gray-400">
                    <LoadingIcon />
                </div>
                <div v-else-if="!listOrder.length" class="flex items-center justify-center h-20 text-gray-400 text-sm italic">
                    Tidak ada order yang tersedia
                </div>
                <ul v-else class="divide-y divide-gray-100">
                    <li
                        v-for="order in listOrder"
                        :key="order.id"
                        class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 select-none"
                        :class="selectedOrderId.includes(order.id) ? 'bg-sky-50' : ''"
                        @click="selectedOrderId.includes(order.id) ? selectedOrderId.splice(selectedOrderId.indexOf(order.id), 1) : selectedOrderId.push(order.id)"
                    >
                        <div
                            class="w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors"
                            :class="selectedOrderId.includes(order.id) ? 'bg-sky-500 border-sky-500 text-white' : 'border-gray-300'"
                        >
                            <FontAwesomeIcon v-if="selectedOrderId.includes(order.id)" icon="fal fa-check" class="text-xs" />
                        </div>
                        <div class="flex-1 min-w-0">
                            <div class="flex items-center gap-2">
                                <span class="font-semibold text-sm">{{ order.reference }}</span>
                                <Icon v-if="order.type_icon" :data="order.type_icon" class="text-xs" />
                            </div>
                            <div class="text-xs text-gray-500 truncate">
                                <span v-if="order.customer?.name">{{ order.customer.name }}</span>
                                <span v-else class="italic">Tanpa pelanggan</span>
                                <span class="mx-1">·</span>
                                <span>{{ useFormatTime(order.created_at) }}</span>
                                <span class="mx-1">·</span>
                                <span>{{ useFormatCurrency(order.total_amount) }}</span>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>

            <p class="mt-2 text-xs text-gray-500">{{ selectedOrderId.length }} order dipilih</p>
            
            <div class="flex justify-end gap-2 mt-4">
                <Button label="Batal" type="negative" @click="isModalOrderList = false" />
                <Button label="Tambahkan" full :disabled="!selectedOrderId.length" :loading="isLoadingOrder" @click="onAddOrder(selectedInvoice?.id)" />
            </div>
        </Dialog>

        <!-- Modal: edit order -->
        <Dialog
            v-model:visible="isModalEditOrder"
            @after-hide="() => selectedEditOrder = null"
            modal
            :header="`Edit Order ${selectedEditOrder?.reference}`"
            :style="{ width: '30rem' }"
            dismissableMask
        >   
            <div v-if="selectedEditOrder" class="pt-1">
                <Button
                    label="Reset modifikasi"
                    type="negative"
                    v-tooltip="'Reset kuantitas, harga, dan total. Tidak mereset persentase diskon.'"
                    icon="fal fa-trash-undo"
                    :disabled="!selectedEditOrder.is_have_discount"
                    :loading="isLoadingEditOrder === selectedEditOrder?.id"
                    full
                    @click="async () => (onResetOrder())"
                />

                <!-- Kuantitas -->
                <div class="mt-6 grid items-center gap-2 mb-4">
                    <label for="username" class="font-medium">
                        Kuantitas
                        <span class="text-gray-400 font-normal">(original: {{ selectedEditOrder.quantity_by_type }})</span>
                    </label>
                    <input
                        v-model="selectedEditOrder.quantity_show"
                        @change="() => delete errors?.quantity_show"
                        type="tel"
                        pattern="[0-9]*"
                        inputmode="numeric"
                        name="input-price"
                        :disabled="isLoadingEditOrder === selectedEditOrder?.id"
                        id="input-price"
                        class="block w-full rounded-md bg-white px-3 py-1.5 text-base placeholder:text-gray-400 focus:ring-sky-300 disabled:bg-gray-200 disabled:text-gray-500 disabled:border-gray-400 sm:text-sm/6"
                        placeholder="999 pcs"
                        aria-describedby="input-price"
                    />
                </div>

                <!-- Harga -->
                <div class="grid items-center gap-2 mb-4">
                    <label for="username" class="font-medium">
                        {{ selectedEditOrder.type === 'flexo' ? 'Harga/cm²' : 'Harga' }}
                        <span class="text-gray-400 font-normal">(original: {{ selectedEditOrder.product_price }})</span>
                    </label>
                    <input
                        v-model="selectedEditOrder.product_price_show"
                        @change="() => delete errors?.product_price_show"
                        type="tel"
                        pattern="[0-9]*"
                        inputmode="numeric"
                        name="input-price"
                        :disabled="isLoadingEditOrder === selectedEditOrder?.id"
                        id="input-price"
                        class="block w-full rounded-md bg-white px-3 py-1.5 text-base placeholder:text-gray-400 focus:ring-sky-300 disabled:bg-gray-200 disabled:text-gray-500 disabled:border-gray-400 sm:text-sm/6"
                        placeholder="999 pcs"
                        aria-describedby="input-price"
                    />
                    <p v-if="selectedEditOrder.type === 'flexo' && selectedEditOrder.dimension_width && selectedEditOrder.dimension_height" class="text-xs text-gray-400 -mt-1">
                        Dimensi: {{ selectedEditOrder.dimension_width }} × {{ selectedEditOrder.dimension_height }} cm
                        &nbsp;·&nbsp; Total = harga × qty × lebar × tinggi
                    </p>
                </div>

                <!-- Persentase Diskon -->
                <div class="grid items-center gap-2 mb-4">
                    <label for="username" class="font-medium">Persentase Diskon</label>
                    <input
                        v-model="selectedEditOrder.discount_in_percentage"
                        @change="() => delete errors?.discount_in_percentage"
                        type="tel"
                        pattern="[0-9]*"
                        inputmode="numeric"
                        name="input-percentage-discount"
                        :disabled="isLoadingEditOrder === selectedEditOrder?.id"
                        id="input-percentage-discount"
                        class="block w-full rounded-md bg-white px-3 py-1.5 text-base placeholder:text-gray-400 focus:ring-sky-300 disabled:bg-gray-200 disabled:text-gray-500 disabled:border-gray-400 sm:text-sm/6"
                        placeholder="0-100"
                        aria-describedby="input-percentage-discount"
                    />
                    <div class="flex gap-x-1 -mt-1">
                        <Button @click="selectedEditOrder.discount_in_percentage = 0" :key="`0${selectedEditOrder.discount_in_percentage === 0}`" :type="selectedEditOrder.discount_in_percentage === 0 ? 'indigo' : 'tertiary'" label="Tanpa diskon" size="xxs" />
                        <Button @click="selectedEditOrder.discount_in_percentage = 5" :key="`5${selectedEditOrder.discount_in_percentage === 5}`" :type="selectedEditOrder.discount_in_percentage === 5 ? 'indigo' : 'tertiary'" label="5%" size="xxs" />
                        <Button @click="selectedEditOrder.discount_in_percentage = 10" :key="`10${selectedEditOrder.discount_in_percentage === 10}`" :type="selectedEditOrder.discount_in_percentage === 10 ? 'indigo' : 'tertiary'" label="10%" size="xxs" />
                        <Button @click="selectedEditOrder.discount_in_percentage = 15" :key="`15${selectedEditOrder.discount_in_percentage === 15}`" :type="selectedEditOrder.discount_in_percentage === 15 ? 'indigo' : 'tertiary'" label="15%" size="xxs" />
                        <Button @click="selectedEditOrder.discount_in_percentage = 20" :key="`20${selectedEditOrder.discount_in_percentage === 20}`" :type="selectedEditOrder.discount_in_percentage === 20 ? 'indigo' : 'tertiary'" label="20%" size="xxs" />
                    </div>
                </div>
                
                <!-- Preview Total -->
                <div v-if="previewTotal" class="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm space-y-1 mb-4">
                    <div class="flex justify-between text-gray-500">
                        <span>Total sebelum</span>
                        <span class="tabular-nums">{{ useFormatCurrency(selectedEditOrder!.total_amount_in_invoice ?? selectedEditOrder!.total_amount) }}</span>
                    </div>
                    <div class="flex justify-between border-t border-dashed pt-1" :class="previewTotal.hasDiscount ? 'text-gray-400' : 'font-semibold'">
                        <span>Total baru</span>
                        <span class="tabular-nums" :class="previewTotal.hasDiscount ? 'line-through' : ''">{{ useFormatCurrency(previewTotal.total) }}</span>
                    </div>
                    <div v-if="previewTotal.hasDiscount" class="flex justify-between font-semibold text-sky-600">
                        <span>Setelah diskon {{ selectedEditOrder!.discount_in_percentage }}%</span>
                        <span class="tabular-nums">{{ useFormatCurrency(previewTotal.afterDiscount) }}</span>
                    </div>
                </div>

                <div class="flex justify-end gap-2">
                    <Button label="Batal" :style="'tertiary'" @click="isModalEditOrder = false"></Button>
                    <Button label="Ubah diskon" :disabled="!true" :loading="isLoadingEditOrder === selectedEditOrder?.id" full @click="onEditOrder()"></Button>
                </div>
            </div>
            <div v-else>
            </div>
        </Dialog>

        <!-- Modal: add payment -->
        <Dialog
            v-model:visible="isPaymentModalOpen"
            modal
            header="Tambah Pembayaran"
            :style="{ width: '32rem' }"
            dismissableMask
        >
            <div class="flex flex-col gap-4 pt-2">
                <!-- Sisa tagihan info -->
                <div class="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm space-y-1">
                    <div class="flex justify-between">
                        <span class="text-gray-500">Total Invoice</span>
                        <span class="font-semibold">{{ useFormatCurrency(invoice.final_total) }}</span>
                    </div>
                    <div class="flex justify-between">
                        <span class="text-gray-500">Sudah Dibayar</span>
                        <span class="font-semibold text-green-600">{{ useFormatCurrency(invoice.amount_paid) }}</span>
                    </div>
                    <div class="flex justify-between border-t border-dashed pt-1">
                        <span class="font-medium">Sisa Tagihan</span>
                        <span class="font-bold text-amber-700">{{ useFormatCurrency(invoice.amount_remaining) }}</span>
                    </div>
                </div>

                <!-- Nominal -->
                <div>
                    <label class="text-sm font-medium text-gray-700 mb-1 block">Nominal Pembayaran</label>
                    <InputNumber
                        v-model="newPaymentAmount"
                        mode="currency"
                        currency="IDR"
                        locale="id-ID"
                        :minFractionDigits="0"
                        :maxFractionDigits="0"
                        :min="1"
                        :max="invoice.amount_remaining"
                        fluid
                        placeholder="Masukkan nominal"
                    />
                    <p class="text-xs text-gray-400 mt-1">Maks: {{ useFormatCurrency(invoice.amount_remaining) }}</p>
                </div>

                <!-- Tanggal Pembayaran -->
                <div>
                    <label class="text-sm font-medium text-gray-700 mb-1 block">Tanggal Pembayaran</label>
                    <DatePicker
                        v-model="newPaymentDate"
                        dateFormat="dd/mm/yy"
                        :maxDate="new Date()"
                        showIcon
                        fluid
                        placeholder="Pilih tanggal"
                    />
                </div>

                <!-- Metode -->
                <div>
                    <label class="text-sm font-medium text-gray-700 mb-1 block">Metode Pembayaran</label>
                    <Select
                        v-model="newPaymentMethod"
                        :options="paymentMethodOptions"
                        optionLabel="label"
                        optionValue="value"
                        class="w-full"
                    />
                </div>

                <!-- Deposit info -->
                <div v-if="newPaymentMethod === 'deposit'" class="rounded-lg border p-3 text-sm space-y-1"
                    :class="(invoice.customer?.deposit_amount ?? 0) >= newPaymentAmount ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'"
                >
                    <div class="flex justify-between">
                        <span class="text-gray-600">Saldo Deposit Pelanggan</span>
                        <span class="font-semibold" :class="(invoice.customer?.deposit_amount ?? 0) >= 0 ? 'text-green-700' : 'text-red-700'">
                            {{ useFormatCurrency(invoice.customer?.deposit_amount ?? 0) }}
                        </span>
                    </div>
                    <div class="flex justify-between border-t border-dashed pt-1">
                        <span class="text-gray-600">Sisa Deposit Setelah Bayar</span>
                        <span class="font-semibold" :class="(invoice.customer?.deposit_amount ?? 0) - newPaymentAmount >= 0 ? 'text-green-700' : 'text-red-700'">
                            {{ useFormatCurrency((invoice.customer?.deposit_amount ?? 0) - newPaymentAmount) }}
                        </span>
                    </div>
                    <p v-if="(invoice.customer?.deposit_amount ?? 0) < newPaymentAmount" class="text-red-600 text-xs mt-1">
                        ⚠ Saldo deposit tidak mencukupi.
                    </p>
                </div>

                <!-- Catatan -->
                <div>
                    <label class="text-sm font-medium text-gray-700 mb-1 block">Catatan <span class="text-gray-400">(opsional)</span></label>
                    <input
                        v-model="newPaymentNote"
                        type="text"
                        class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-sky-300 focus:outline-none"
                        placeholder="Catatan pembayaran..."
                    />
                </div>
            </div>

            <template #footer>
                <Button label="Batal" type="cancel" @click="isPaymentModalOpen = false" />
                <Button
                    label="Catat Pembayaran"
                    full
                    :loading="isLoadingPaid === invoice.id"
                    :disabled="!newPaymentAmount || newPaymentAmount < 1 || (newPaymentMethod === 'deposit' && (invoice.customer?.deposit_amount ?? 0) < newPaymentAmount)"
                    @click="confirmAddPayment"
                />
            </template>
        </Dialog>

        <!-- Modal: edit payment -->
        <Dialog
            v-model:visible="isEditPaymentModalOpen"
            modal
            header="Edit Pembayaran"
            :style="{ width: '28rem' }"
            dismissableMask
        >
            <div class="flex flex-col gap-4 pt-2">
                <div>
                    <label class="text-sm font-medium text-gray-700 mb-1 block">Tanggal Pembayaran</label>
                    <DatePicker
                        v-model="editPaymentDate"
                        dateFormat="dd/mm/yy"
                        :maxDate="new Date()"
                        showIcon
                        fluid
                        placeholder="Pilih tanggal"
                    />
                </div>

                <div>
                    <label class="text-sm font-medium text-gray-700 mb-1 block">Catatan <span class="text-gray-400">(opsional)</span></label>
                    <input
                        v-model="editPaymentNote"
                        type="text"
                        class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-sky-300 focus:outline-none"
                        placeholder="Catatan pembayaran..."
                    />
                </div>
            </div>

            <template #footer>
                <Button label="Batal" type="cancel" @click="isEditPaymentModalOpen = false" />
                <Button
                    label="Simpan"
                    full
                    :loading="isLoadingEditPayment"
                    @click="confirmEditPayment"
                />
            </template>
        </Dialog>

        <!-- Modal: add additional cost -->
        <Dialog
            v-model:visible="isAdditionalCostModalOpen"
            modal
            header="Tambah Ongkos Tambahan"
            :style="{ width: '30rem' }"
            dismissableMask
        >
            <div class="flex flex-col gap-4 pt-2">
                <!-- Nominal -->
                <div>
                    <label class="text-sm font-medium text-gray-700 mb-1 block"><span class="text-red-500">*</span> Nominal</label>
                    <InputNumber
                        v-model="newCostAmount"
                        mode="currency"
                        currency="IDR"
                        locale="id-ID"
                        :minFractionDigits="0"
                        :maxFractionDigits="0"
                        :min="0"
                        fluid
                        placeholder="Masukkan nominal"
                    />
                </div>

                <!-- Deskripsi -->
                <div>
                    <label class="text-sm font-medium text-gray-700 mb-1 block">
                        <span class="text-red-500">*</span> Deskripsi
                    </label>
                    <input
                        v-model="newCostDescription"
                        type="text"
                        class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-sky-300 focus:outline-none"
                        placeholder="Contoh: Ongkos kirim, biaya desain..."
                        maxlength="254"
                    />
                </div>

                <!-- Tanggal -->
                <div>
                    <label class="text-sm font-medium text-gray-700 mb-1 block">Tanggal <span class="text-gray-400">(opsional)</span></label>
                    <DatePicker
                        v-model="newCostDate"
                        dateFormat="dd/mm/yy"
                        showIcon
                        fluid
                        placeholder="Pilih tanggal"
                    />
                </div>
            </div>

            <template #footer>
                <Button label="Batal" type="cancel" @click="isAdditionalCostModalOpen = false" />
                <Button
                    label="Tambahkan"
                    full
                    :loading="isLoadingAdditionalCost"
                    :disabled="!newCostAmount || newCostAmount < 1 || !newCostDescription.trim()"
                    @click="confirmAddAdditionalCost"
                />
            </template>
        </Dialog>
    </section>
</template>